import React, { useState } from 'react'
import classNames from 'classnames'
import { RemoveScroll } from 'react-remove-scroll'

import { mq } from 'marketing-site/src/library/utils'
import { Text } from 'marketing-site/src/library/elements/Text'
import { Icon } from '../../../elements/Icon'
import { HeaderNavigationCTA } from './HeaderNavigationCTA'
import {
  IHeaderNavigationMobile,
  isHeaderNavigationMenuDescription,
  isHeaderNavigationMenu,
  isHeaderNavigationMenuSection,
  isHeaderNavigationSolutions,
  isHeaderNavigationSpotlight,
} from '..'
import { HeaderNavigationMenuSection } from './HeaderNavigationMenuSection'
import { HeaderNavigationSpotlight } from './HeaderNavigationSpotlight'
import { HeaderNavigationMenuDescription } from './HeaderNavigationMenuDescription'
import { HeaderNavigationSolutions } from './HeaderNavigationSolutions'
import { HeaderNavigationSignInLink } from './HeaderNavigationSignInLink'
import {
  ICtaButtonMvp as IContentfulCtaButtonMvp,
  ICtaLinkMvp as IContentfulCtaLinkMvp,
  ISignupCta as IContentfulSignupCta,
  IVariationContainer as IContentfulVariationContainer,
} from 'marketing-site/@types/generated/contentful'
import { CTATheme as CTAThemeMVP } from 'marketing-site/src/mvp/library/utils'

export const HeaderNavigationMobile = ({
  contentType = 'flex',
  theme = 'light',
  navigationElements,
  cta,
  secondCta,
  signInLink,
  topContentHeight = null,
  isMobileMenuActive = false,
}: IHeaderNavigationMobile): React.ReactElement => {
  const [activeItem, setActiveItem] = useState<number | null>(null)

  const scrollToElement = (index: number) => {
    setActiveItem((currentItem) => {
      return index === currentItem ? null : index
    })
  }

  const getDynamicCTA = () => {
    switch (cta?.sys.contentType.sys.id) {
      case 'ctaLinkMvp':
      case 'ctaButtonMvp':
        return {
          ...cta,
          fields: {
            ...cta.fields,
            variant: CTAThemeMVP.BlueFill,
          },
        } as IContentfulCtaButtonMvp | IContentfulCtaLinkMvp
      default:
        return cta as IContentfulSignupCta | IContentfulVariationContainer
    }
  }
  const getDynamicSecondCTA = () => {
    switch (secondCta?.sys.contentType.sys.id) {
      case 'ctaLinkMvp':
      case 'ctaButtonMvp':
        return {
          ...secondCta,
          fields: {
            ...secondCta.fields,
            variant: theme === 'light' ? CTAThemeMVP.SlateOutline : CTAThemeMVP.PaperOutline,
          },
        } as IContentfulCtaButtonMvp | IContentfulCtaLinkMvp
      default:
        return secondCta as IContentfulSignupCta | IContentfulVariationContainer
    }
  }

  const itemsClasses = classNames(
    'header-navigation-mobile__items',
    RemoveScroll.classNames.zeroRight,
  )

  const classes = classNames('header-navigation-mobile', {
    'header-navigation-mobile--on': isMobileMenuActive,
    'header-navigation-mobile--off': !isMobileMenuActive,
    [`header-navigation-mobile--theme-${theme}`]: theme,
  })

  return (
    <>
      <div className={classes}>
        <ul className={itemsClasses}>
          {navigationElements &&
            navigationElements.map((item, index) => {
              const itemClasses = classNames('header-navigation-mobile-item', {
                'header-navigation-mobile-item--is-active': activeItem === index,
              })
              return (
                <li
                  className={itemClasses}
                  key={index}
                  id={`header-navigation-mobile-item-${index}`}
                >
                  {isHeaderNavigationMenu(item) ? (
                    <>
                      <button
                        className="header-navigation-mobile-item__header"
                        onClick={() => scrollToElement(index)}
                      >
                        <div className="header-navigation-mobile-item__title">
                          <Text brand={contentType === 'flex' ? 'default' : 'mvp'} size="heading-3">
                            {item.title}
                          </Text>
                        </div>
                        <Icon icon="caret-down" size="medium" />
                      </button>
                      <div className="header-navigation-mobile-item__content">
                        {item.content.map((element, i) => {
                          if (isHeaderNavigationMenuDescription(element)) {
                            element.subhead && (
                              <HeaderNavigationMenuDescription
                                key={i}
                                {...element}
                                contentType={contentType}
                              />
                            )
                          } else if (isHeaderNavigationMenuSection(element)) {
                            return (
                              <HeaderNavigationMenuSection
                                key={i}
                                {...element}
                                contentType={contentType}
                                theme={theme}
                              />
                            )
                          } else if (isHeaderNavigationSolutions(element)) {
                            return (
                              <HeaderNavigationSolutions
                                key={i}
                                {...element}
                                contentType={contentType}
                              />
                            )
                          }
                        })}
                        {item.sidebar.map((element, i) => {
                          if (isHeaderNavigationSpotlight(element)) {
                            return (
                              <div
                                key={i}
                                className="header-navigation-mobile-item__content--spotlight"
                              >
                                <HeaderNavigationSpotlight
                                  {...element}
                                  contentType={contentType}
                                  theme={theme}
                                />
                              </div>
                            )
                          } else if (isHeaderNavigationMenuSection(element)) {
                            return (
                              <div key={i} className="header-navigation-mobile-item__sidebar">
                                <HeaderNavigationMenuSection
                                  {...element}
                                  contentType={contentType}
                                  theme={theme}
                                />
                              </div>
                            )
                          }
                        })}
                      </div>
                    </>
                  ) : (
                    <a className="header-navigation-mobile-item__header" href={item.url}>
                      <div className="header-navigation-mobile-item__title header-navigation-mobile-item__title--anchor">
                        <Text brand={contentType === 'flex' ? 'default' : 'mvp'} size="heading-3">
                          {item.title}
                        </Text>
                      </div>
                    </a>
                  )}
                </li>
              )
            })}
          <li className="header-navigation-mobile-item">
            {signInLink && <HeaderNavigationSignInLink {...signInLink} contentType={contentType} />}
          </li>
        </ul>

        {(cta || secondCta) && (
          <div className="header-navigation-mobile__ctas">
            {cta && <HeaderNavigationCTA cta={getDynamicCTA()} />}
            {secondCta && <HeaderNavigationCTA cta={getDynamicSecondCTA()} />}
          </div>
        )}
      </div>

      <style jsx>{`
        .header-navigation-mobile {
          width: 100%;
          display: flex;
          flex-direction: column;
          height: calc(100vh - ${topContentHeight}px);
          position: absolute;
          right: 0;
          transition: background-color 0.32s;

          &--on {
            visibility: visible;
            opacity: 1;
            z-index: 1;
          }

          &--off {
            visibility: hidden;
            opacity: 0;
          }

          &__items {
            display: flex;
            flex-direction: column;
            width: 100%;
            overflow-y: auto;
          }

          &__ctas {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            margin-top: auto;
            padding: 1.5rem 1.25rem;

            :global(.header-navigation-cta .cta-link) {
              width: 100%;
            }
          }

          &--theme-light {
            background-color: ${contentType === 'flex' ? 'var(--white)' : 'var(--paper)'};

            .header-navigation-mobile-item__header,
            :global(.header-navigation-mobile-item__title) {
              color: ${contentType === 'flex' ? 'var(--slate)' : 'var(--mvp-slate)'};
            }

            .header-navigation-mobile-item__header {
              background-color: var(--paper);

              &::after {
                background-color: var(--neutral-300);
              }
            }

            .header-navigation-mobile-item__content {
              background-color: ${contentType === 'flex' ? 'var(--stone)' : 'var(--mvp-stone)'};
            }
          }

          &--theme-dark {
            background-color: ${contentType === 'flex' ? 'var(--slate)' : 'var(--mvp-slate)'};

            .header-navigation-mobile-item__header,
            :global(.header-navigation-mobile-item__title) {
              color: var(--paper);
            }

            .header-navigation-mobile-item__header {
              background-color: ${contentType === 'flex' ? 'var(--slate)' : 'var(--mvp-slate)'};

              &::after {
                background-color: var(--neutral-600);
              }
            }

            .header-navigation-mobile-item__content {
              background-color: var(--neutral-800);
            }
          }

          &--theme-resolution-bot {
            background-color: black;

            .header-navigation-mobile-item__header,
            :global(.header-navigation-mobile-item__title) {
              color: var(--paper);
            }

            .header-navigation-mobile-item__header {
              background-color: ${contentType === 'flex' ? 'var(--slate)' : 'var(--mvp-slate)'};

              &::after {
                background-color: var(--neutral-600);
              }
            }

            .header-navigation-mobile-item__content {
              background-color: var(--neutral-800);
            }
          }
        }

        .header-navigation-mobile-item {
          &__header {
            width: 100%;
            display: grid;
            grid-column-gap: 1.25rem;
            grid-template-columns: 1fr auto;
            justify-content: space-between;
            align-items: center;
            padding: 0 1.25rem;
            text-decoration: none;
            cursor: pointer;
            user-select: none;
            transition: background-color 0.32s;

            &::after {
              display: block;
              grid-column: 1/-1;
              grid-row: 2;
              content: '';
              position: relative;
              width: 100%;
              height: 1px;
              background-color: var(--neutral-300);
              transition: height 0.24s 0.4s;
            }
          }

          &__title {
            display: inline-block;
            width: 100%;
            padding: 1.25rem 0;
            white-space: nowrap;

            &--anchor {
              text-decoration: none;
              color: ${contentType === 'flex' ? 'var(--slate)' : 'var(--mvp-slate)'};
            }
          }

          :global(.icon) {
            display: inline-block;
            vertical-align: middle;

            path {
              transition: fill 0.32s, transform 0.32s;
            }
          }

          &__content {
            max-height: 0;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            padding: 0 1.25rem;
            position: relative;
            transition: max-height 0.6s, padding 0.6s, background-color 0.32s;

            & > :global(*:not(:last-child)::after) {
              display: block;
              content: '';
              position: relative;
              width: 100%;
              height: 1px;
              margin: 1.75rem 0;
              background-color: var(--neutral-300);
              transition: background-color 0.32s;
            }
          }

          &__sidebar {
            :global(.header-navigation-section__items-column) {
              flex-flow: row wrap;
            }

            :global(.header-navigation-menu-item) {
              flex: 50%;
            }

            :global(.cta-link) {
              &:focus,
              &:hover {
                text-decoration: underline;
              }
            }
          }

          &--is-active {
            .header-navigation-mobile-item__header {
              &::after {
                height: 0;
                transition: height 0.5s;
              }

              & > :global(.icon) {
                transform: rotateX(180deg);
              }
            }

            .header-navigation-mobile-item__content {
              max-height: 75rem;
              padding: 1.5rem 1.25rem;
            }
          }
        }

        @media (${mq.tablet}) {
          .header-navigation-mobile {
            width: 26.5rem;
          }

          .header-navigation-mobile-item {
            &__header {
              padding: 0 1.25rem;
              margin: 0;
            }

            &__content {
              padding: 0 1.25rem;
              margin: 0;
            }

            &__title :global(span),
            :global(.header-navigation-mobile-item__title span) {
              font-size: ${contentType === 'flex' ? '1.75rem' : '1.875rem'};
              line-height: 1.32;
            }

            &--is-active {
              .header-navigation-mobile-item__content {
                padding: 1.5rem 1.25rem;
              }
            }
          }
        }
      `}</style>
    </>
  )
}
